import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  Recycle,
  ShieldCheck,
  Clock,
  Sofa,
  Refrigerator,
  Trees,
  Trash2,
  MapPin,
  Phone,
  Camera,
  ClipboardList,
  CalendarCheck,
  Heart,
  Handshake,
  Users,
} from "lucide-react";
import { LOGO_URL, COMPANY } from "@/lib/brand";
import SiteFooter from "@/components/SiteFooter";
import BookCTA from "@/components/BookCTA";
import Header from "@/components/Header";
import GoogleReviewsBadge from "@/components/GoogleReviewsBadge";
import HomeGalleryStrip from "@/components/HomeGalleryStrip";
import { usePageMeta } from "@/lib/seo";
import { findArea } from "@/lib/areas";

const HOME_AREAS = ["peterborough", "oundle", "yaxley", "whittlesey", "stamford", "market-deeping", "bourne", "ramsey", "huntingdon"].map(findArea);

const services = [
  { icon: Truck, title: "Full house clearances", body: "End-of-tenancy, probate and downsizing clearances handled top to bottom." },
  { icon: Sofa, title: "Furniture removal", body: "Sofas, wardrobes, mattresses, dining sets — taken apart and hauled away." },
  { icon: Refrigerator, title: "White goods & electricals", body: "Fridges, washers, cookers and TVs disposed of the correct, legal way." },
  { icon: Trees, title: "Garden & outbuildings", body: "Sheds, greenhouses, garden waste and BBQs cleared in a single visit." },
  { icon: Trash2, title: "Bagged rubbish", body: "Loose bags, boxes and post-renovation debris — no volume too small." },
  { icon: Recycle, title: "Eco-friendly disposal", body: "We recycle, donate and reuse wherever possible — landfill is a last resort." },
];

const steps = [
  { icon: ClipboardList, title: "Tell us what you need", body: "Fill in five short steps with your address, items, room photos and preferred date." },
  { icon: Camera, title: "Upload a few photos", body: "Room photos help us bring the right van and crew size." },
  { icon: CalendarCheck, title: "Pick a date", body: "Choose a preferred day that suits you and we'll confirm by email." },
  { icon: Truck, title: "We collect & clear", body: "One team, one visit — no mess left behind." },
];

const trustBadges = [
  { icon: ShieldCheck, label: "Fully insured" },
  { icon: Recycle, label: "Licensed carriers" },
  { icon: Clock, label: "Same-week slots" },
];

export default function Home() {
  usePageMeta(
    "House Clearance Peterborough | Waites Clearances",
    "House clearance Peterborough — a family run, fully licensed and insured clearance team based in Peterborough. Free quotes, eco-friendly disposal, serving Peterborough and surrounding areas."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="home-page">
      <Header />

      {/* ─────────────────────────────── HERO ─────────────────────────────── */}
      <section className="relative min-h-[calc(100svh-5rem)] w-full bg-slate-900" data-testid="home-hero">
        <img
          src="https://images.unsplash.com/photo-1554995207-c18c203602cb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="Cleared living room"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/40 to-black/80" />

        <div className="relative z-10 min-h-[calc(100svh-5rem)] flex flex-col px-6 sm:px-10 pt-10 pb-10 max-w-xl mx-auto">
          <div className="flex-1" />

          <div className="pb-8">
            <h1
              data-testid="home-headline"
              className="font-display text-white text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.1]"
            >
              House Clearance Peterborough &amp; surrounding areas — Fast, Fully Licensed &amp; Insured.
            </h1>
            <p
              data-testid="home-subheadline"
              className="mt-5 text-white/90 text-base sm:text-lg leading-relaxed max-w-md"
            >
              Tell us what you need collected and we'll handle the rest. Furniture, appliances,
              garden waste — one team, one visit.
            </p>
            <p data-testid="home-service-area" className="mt-3 text-white/70 text-sm">
              Serving{" "}
              <Link to="/areas/peterborough" data-testid="home-hero-area-peterborough" className="underline decoration-white/40 underline-offset-4 hover:text-white hover:decoration-[#FF7F00] transition-colors">
                Peterborough
              </Link>{" "}
              and the{" "}
              <Link to="/areas" data-testid="home-hero-areas-link" className="underline decoration-white/40 underline-offset-4 hover:text-white hover:decoration-[#FF7F00] transition-colors">
                surrounding areas
              </Link>
              .
            </p>
          </div>

          <div>
            <Link
              to="/book"
              data-testid="home-cta-book"
              className="group flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-[#FF7F00] hover:bg-[#E67000] text-white text-base font-bold shadow-xl shadow-black/30 transition-colors"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href="https://wa.me/447761748698"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-cta-whatsapp"
              className="mt-3 group flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-[#25D366] hover:bg-[#1DA851] text-white text-base font-bold shadow-xl shadow-black/30 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="currentColor">
                <path d="M20.52 3.48A11.86 11.86 0 0 0 12.04 0C5.5 0 .18 5.3.18 11.84c0 2.08.55 4.11 1.6 5.9L0 24l6.42-1.69a11.83 11.83 0 0 0 5.62 1.43h.01c6.54 0 11.86-5.3 11.86-11.84 0-3.16-1.23-6.14-3.39-8.42zM12.04 21.5h-.01a9.65 9.65 0 0 1-4.92-1.35l-.35-.21-3.81 1 1.02-3.72-.23-.38A9.6 9.6 0 0 1 2.4 11.84c0-5.33 4.33-9.66 9.65-9.66 2.58 0 5 1 6.82 2.82a9.57 9.57 0 0 1 2.83 6.84c0 5.33-4.32 9.66-9.66 9.66zm5.3-7.23c-.29-.14-1.72-.85-1.99-.95-.27-.1-.46-.14-.66.14-.19.29-.76.95-.93 1.14-.17.19-.34.21-.63.07-.29-.14-1.23-.46-2.34-1.45-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.44.13-.59.13-.13.29-.34.43-.52.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.66-1.59-.91-2.17-.24-.57-.48-.49-.66-.5l-.57-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.38 0 1.4 1.03 2.76 1.17 2.95.14.19 2.02 3.08 4.89 4.32.68.3 1.21.47 1.63.6.68.22 1.3.19 1.79.11.55-.08 1.72-.7 1.96-1.38.24-.68.24-1.26.17-1.38-.07-.12-.26-.19-.55-.33z"/>
              </svg>
              Chat on WhatsApp
            </a>
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="home-cta-call"
              className="mt-3 group flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-[#0F172A] hover:bg-[#1E293B] border border-white/15 text-white text-base font-bold shadow-xl shadow-black/30 transition-colors"
            >
              <Phone className="h-5 w-5 text-[#FF9933]" />
              Call Us: {COMPANY.phoneDisplay}
            </a>
            <p className="mt-3 text-center text-xs text-white/60">Free quote • No obligation</p>
          </div>
        </div>

        <a
          href="#about"
          data-testid="hero-scroll-hint"
          className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 text-white/70 text-[11px] uppercase tracking-widest hover:text-white"
        >
          Learn more ↓
        </a>
      </section>

      {/* ─────────────────────────────── GOOGLE REVIEWS TRUST BADGE ─────────────────────────────── */}
      <GoogleReviewsBadge />

      {/* ─────────────────────────────── LICENCE LINE ─────────────────────────────── */}
      <div className="bg-white pb-10 sm:pb-14" data-testid="home-licence-line">
        <p className="max-w-5xl mx-auto px-6 sm:px-10 -mt-4 text-center text-xs text-slate-500">
          Waste Carrier Licence No.{" "}
          <span className="font-semibold text-slate-700">{COMPANY.wasteCarrierLicence}</span>
        </p>
      </div>

      {/* ─────────────────────────────── SEE OUR WORK ─────────────────────────────── */}
      <HomeGalleryStrip />

      {/* ─────────────────────────────── ABOUT ─────────────────────────────── */}
      <section id="about" className="py-20 sm:py-28" data-testid="section-about">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">
                About us
              </p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                A family run business, born in Peterborough.
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Waites Clearances is a small,{" "}
                <Link
                  to="/team"
                  data-testid="home-family-run-text-link"
                  className="font-semibold text-[#FF7F00] underline decoration-[#FFB870] underline-offset-4 hover:text-[#B85800]"
                >
                  family run team
                </Link>{" "}
                based right here in Peterborough. We
                started out helping neighbours, friends and local landlords with the heavy lifting —
                and we've kept that same approach ever since. When you book with us you're not
                dealing with a call centre or a national chain: it's a real family answering the
                phone and turning up on the day.
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                We treat every home with the respect we'd want for our own, whether it's a
                single-item collection or an entire property to clear. Honest quotes, tidy work,
                nothing left behind.
              </p>

              <div className="mt-8 grid sm:grid-cols-3 gap-3">
                <Link
                  to="/team"
                  data-testid="home-family-run-card-link"
                  className="group rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:border-[#FFB870] hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="h-10 w-10 rounded-xl bg-[#FFF3E6] flex items-center justify-center">
                    <Users className="h-5 w-5 text-[#FF7F00]" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-slate-900 text-sm group-hover:text-[#FF7F00] transition-colors">Family run</p>
                    <p className="text-xs text-slate-500">Meet the team &rarr;</p>
                  </div>
                </Link>
                <Link
                  to="/areas"
                  data-testid="home-peterborough-card-link"
                  className="group rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:border-[#FFB870] hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="h-10 w-10 rounded-xl bg-[#FFF3E6] flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-[#FF7F00]" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-slate-900 text-sm group-hover:text-[#FF7F00] transition-colors">Peterborough</p>
                    <p className="text-xs text-slate-500">Based &amp; working here</p>
                  </div>
                </Link>
                <Link
                  to="/book"
                  data-testid="home-honest-quotes-card-link"
                  className="group rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 hover:border-[#FFB870] hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="h-10 w-10 rounded-xl bg-[#FFF3E6] flex items-center justify-center">
                    <Handshake className="h-5 w-5 text-[#FF7F00]" />
                  </div>
                  <div>
                    <p className="font-display font-semibold text-slate-900 text-sm group-hover:text-[#FF7F00] transition-colors">Honest quotes</p>
                    <p className="text-xs text-slate-500">No pressure, ever</p>
                  </div>
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 bg-white p-6 shadow-lg">
                <div className="flex items-center gap-4">
                  <img
                    src={LOGO_URL}
                    alt="Waites Clearances logo"
                    className="h-20 w-20 rounded-2xl object-contain bg-white border border-slate-200"
                  />
                  <div>
                    <p className="font-display font-extrabold text-slate-900 text-lg">
                      Waites <span className="text-[#FF7F00]">Clearances</span>
                    </p>
                    <p className="text-sm text-slate-500">Family run since day one</p>
                  </div>
                </div>
                <div className="mt-6 rounded-2xl bg-[#FFF7EE] border border-[#FFB870] p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#B85800]">
                    A word from us
                  </p>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed italic">
                    "We treat every clearance like we're doing it for family — because most of the
                    time, we're doing it for someone in the neighbourhood."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────── SERVICES ─────────────────────────────── */}
      <section id="services" className="py-20 sm:py-28 bg-white border-y border-slate-100" data-testid="section-services">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">What we clear</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              From a single sofa to a whole property.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether it's one awkward item or an entire estate, we'll turn up with the right van,
              the right crew and a plan to get it done in one visit.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map(({ icon: Icon, title, body }) => (
              <Link
                key={title}
                to="/services"
                data-testid={`home-service-card-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group relative block rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#FFB870] hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7F00]"
              >
                <div className="h-11 w-11 rounded-xl bg-[#FFF3E6] flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 group-hover:text-[#FF7F00] transition-colors">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
            {trustBadges.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 text-sm text-slate-600">
                <Icon className="h-4 w-4 text-[#FF7F00]" /> {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────────── PROBATE & BEREAVEMENT ─────────────────────────────── */}
      <section className="py-20 sm:py-28" data-testid="section-care">
        <div className="max-w-5xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] to-white p-8 sm:p-12">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-2xl bg-white border border-[#FFB870] flex items-center justify-center">
                <Heart className="h-5 w-5 text-[#FF7F00]" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#B85800]">
                Handled with care
              </p>
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Probate &amp; bereavement clearances.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
              Clearing a loved one's home is never easy. Our family understands how sensitive this
              time is, and we'll work to your pace — quietly, respectfully and without judgment.
              We can co-ordinate with solicitors, executors and estate agents where needed, and
              we're happy to set aside anything you'd like to keep, donate or pass on before we
              start.
            </p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="care-probate">
                <p className="font-display text-lg font-semibold text-slate-900">Probate clearances</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  A discreet, thorough clearance of the estate — inventoried where you need it and
                  timed around solicitors and property sale dates.
                </p>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="care-bereavement">
                <p className="font-display text-lg font-semibold text-slate-900">Bereavement clearances</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  A gentle, patient service for families who need a hand clearing a loved one's
                  home. Personal items are always set aside with you first.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/book"
                data-testid="care-cta-book"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href={`tel:${COMPANY.phoneTel}`}
                data-testid="care-cta-phone"
                className="inline-flex items-center gap-2 text-slate-700 hover:text-[#FF7F00] font-semibold"
              >
                <Phone className="h-4 w-4" /> Prefer to talk? Call {COMPANY.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────── HOW IT WORKS ─────────────────────────────── */}
      <section className="py-20 sm:py-28 bg-white border-y border-slate-100" data-testid="section-how">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">How it works</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Booked online in under three minutes.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                No phone tag, no in-person survey. Send us the details and photos — we'll confirm
                by email and turn up on the day.
              </p>
              <Link
                to="/book"
                data-testid="how-cta-book"
                className="mt-8 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white font-bold shadow-md shadow-orange-200 transition-colors"
              >
                Get a Free Quote <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <ol className="lg:col-span-8 space-y-4">
              {steps.map(({ icon: Icon, title, body }, i) => (
                <li
                  key={title}
                  className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 hover:border-[#FFB870] transition-colors"
                >
                  <span className="h-10 w-10 rounded-xl bg-[#0F172A] text-white text-sm font-black flex items-center justify-center shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-[#FF7F00]" />
                      <h3 className="font-display font-semibold text-slate-900">{title}</h3>
                    </div>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────── SERVICE AREA ─────────────────────────────── */}
      <section className="py-20 sm:py-28" data-testid="section-area">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">Service area</p>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
                Based in Peterborough. Working nearby.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed max-w-lg">
                We cover Peterborough and the surrounding towns and villages. If you're unsure
                whether we come out to your postcode, submit a booking and we'll confirm within
                the day.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {HOME_AREAS.map(({ slug, name }) => (
                  <Link
                    key={slug}
                    to={`/areas/${slug}`}
                    data-testid={`home-area-pill-${slug}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 hover:border-[#FF7F00] hover:text-[#FF7F00] hover:bg-[#FFF7EE] active:scale-[0.97] transition-[color,background-color,border-color,transform]"
                  >
                    <MapPin className="h-3.5 w-3.5 text-[#FF7F00]" /> {name}
                  </Link>
                ))}
                <Link
                  to="/areas"
                  data-testid="home-area-pill-all"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#FFB870] bg-[#FFF3E6] px-3 py-1.5 text-sm font-semibold text-[#B85800] hover:bg-[#FFE7CC] transition-colors"
                >
                  All areas <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Waites Clearances van"
                  className="w-full h-72 sm:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
                <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-white/70">Book online</p>
                    <p className="mt-1 text-white font-bold">Pick a date that suits you</p>
                  </div>
                  <Link
                    to="/book"
                    data-testid="area-cta-book"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 h-10 px-4 font-semibold text-sm hover:bg-[#FFF3E6]"
                  >
                    Book <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────── FOOTER CTA ─────────────────────────────── */}
      <BookCTA />

      {/* ─────────────────────────────── CONTACT / FOOTER ─────────────────────────────── */}
      <SiteFooter />
    </div>
  );
}
